import { Fragment } from 'react';
import { StyleSheet, View } from 'react-native';

import type { PageBuilderFooter } from '@fhs/cms-groq-queries';
import { Text, XStack, YStack, createMqStyles, tokens, useMqSelect } from '@fhs/ui';

import { ActionLink } from '../components/action-link';
import { BrightEdgeContainer } from '../components/brightedge-container';
import { Icon } from '../components/icon';
import { SimpleBlockContent } from '../components/simple-block-content';
import type { ActionHandler } from '../types';
import { addActionSource } from '../utils/action';

export type LayoutFooterProps = Pick<
  PageBuilderFooter,
  'legalLinks' | 'legalText' | 'linkGroups' | 'socialLinks'
> & {
  onAction: ActionHandler;
};

export function LayoutFooter(props: LayoutFooterProps) {
  const mqStyles = useMqStyles();
  const iconSize = useMqSelect({ $gteDesktop: 24 }, 20);

  return (
    <YStack style={mqStyles.footerContainer}>
      <YStack style={mqStyles.footerContentArea}>
        <View style={mqStyles.aboveDividerContentArea}>
          <View style={mqStyles.linkGroupArea}>
            {props.linkGroups.map(linkGroup => {
              return (
                <YStack style={mqStyles.linkGroup} key={linkGroup._key}>
                  {(linkGroup.textLinks ?? []).map(textLink => {
                    return (
                      <ActionLink
                        key={textLink._key}
                        onAction={addActionSource(
                          { component: 'text-link', componentType: 'link' },
                          props.onAction
                        )}
                        action={textLink.link}
                      >
                        <Text.Paragraph size="sm">{textLink.text}</Text.Paragraph>
                      </ActionLink>
                    );
                  })}
                </YStack>
              );
            })}
          </View>
          <XStack style={mqStyles.socialLinkArea}>
            {(props.socialLinks ?? []).map(socialLink => {
              return (
                <ActionLink
                  key={socialLink._key}
                  action={socialLink.link}
                  onAction={addActionSource(
                    { component: 'social-link', componentType: 'link' },
                    props.onAction
                  )}
                >
                  <Icon size={iconSize} color={tokens.colors.$houseDark} name={socialLink.icon} />
                </ActionLink>
              );
            })}
          </XStack>
        </View>
        <HorizontalDivider />
        <BrightEdgeContainer divider={<HorizontalDivider />} />
        <View style={mqStyles.belowDividerContentArea}>
          <XStack>
            {props.legalText && (
              <SimpleBlockContent
                onAction={addActionSource(
                  { component: 'legal-text', componentType: 'block' },
                  props.onAction
                )}
                value={props.legalText}
                normalText={{ style: mqStyles.legalText, size: 'sm' }}
                linkText={{ style: mqStyles.legalTextLink }}
              />
            )}
          </XStack>
          <XStack style={mqStyles.legalLinksArea}>
            {(props.legalLinks ?? []).map((legalLink, idx) => {
              const isLast = idx === props.legalLinks.length - 1;

              return (
                <Fragment key={legalLink._key}>
                  <ActionLink
                    action={legalLink.link}
                    onAction={addActionSource(
                      { component: 'legal-link', componentType: 'link' },
                      props.onAction
                    )}
                  >
                    {'text' in legalLink && (
                      <Text.Paragraph size="sm" style={mqStyles.legalLink}>
                        {legalLink.text}
                      </Text.Paragraph>
                    )}
                    {'icon' in legalLink && (
                      <Icon color={tokens.colors.$blackOpacity75} name={legalLink.icon} />
                    )}
                  </ActionLink>
                  {!isLast && <VerticalDivider />}
                </Fragment>
              );
            })}
          </XStack>
        </View>
      </YStack>
    </YStack>
  );
}

const useMqStyles = createMqStyles({
  footerContainer: {
    $base: {
      borderTopWidth: 1,
      borderTopColor: tokens.colors.$blackOpacity10,
      backgroundColor: tokens.colors.$houseLight,
      paddingTop: 56,
      paddingBottom: 40,
      marginTop: 24,
    },
    $gteDesktop: {
      marginTop: 32,
    },
  },
  footerContentArea: {
    $base: {
      width: '100%',
      maxWidth: 1400,
      marginHorizontal: 'auto',
      paddingHorizontal: 32,
      gap: 32,
    },
    $gteDesktop: {
      gap: 40,
    },
  },
  aboveDividerContentArea: {
    $base: {
      width: '100%',
      justifyContent: 'space-between',
      gap: 32,
    },
    $gteDesktop: {
      flexDirection: 'row',
    },
  },
  linkGroupArea: {
    $base: {
      flexGrow: 1,
      gap: 32,
    },
    $gteDesktop: {
      flexDirection: 'row',
    },
  },
  linkGroup: {
    $base: {
      maxWidth: 210,
      flexGrow: 1,
      gap: 8,
    },
  },
  socialLinkArea: {
    $base: {
      alignSelf: 'flex-start',
      flexShrink: 1,
      flexGrow: 0,
      gap: 32,
      flexWrap: 'wrap',
    },
    $gteDesktop: {
      justifyContent: 'flex-end',
    },
  },

  belowDividerContentArea: {
    $base: {
      width: '100%',
      maxWidth: '100%',
      justifyContent: 'space-between',
      gap: 32,
    },
    $gteDesktop: {
      flexDirection: 'row',
    },
  },
  legalText: {
    $base: {
      color: tokens.colors.$blackOpacity75,
      maxWidth: '100%',
    },
  },
  legalTextLink: {
    $base: {
      flexShrink: 1,
      flexWrap: 'wrap',
      color: tokens.colors.$blackOpacity75,
      textDecorationLine: 'underline',
      textDecorationColor: tokens.colors.$blackOpacity55,
    },
  },
  legalLinksArea: {
    $base: {
      gap: 16,
      flexWrap: 'wrap',
    },
  },
  legalLink: {
    $base: {
      color: tokens.colors.$blackOpacity75,
    },
  },
});

function HorizontalDivider() {
  return <View style={dividerStyles.horizontalDivider} />;
}

function VerticalDivider() {
  return <View style={dividerStyles.verticalDivider} />;
}

const dividerStyles = StyleSheet.create({
  horizontalDivider: {
    width: '100%',
    height: 1,
    backgroundColor: tokens.colors.$blackOpacity10,
  },
  verticalDivider: {
    width: 1,
    backgroundColor: tokens.colors.$blackOpacity10,
  },
});
